/**
 * SeasonTransitionNotice — the Season_Transition_Notice shown to a prospective
 * Applicant after the active Season's Phase 1 has closed
 * (season-phase-deadlines spec, Task 16).
 *
 * Requirement 15 (summary):
 *   - WHILE the active Season's Phase_1_Close_Date (`intakeWindow.closesAt`) is
 *     PRESENT AND the Evaluation_Instant is AT/AFTER it, AND the viewer has NO
 *     `SUBMITTED`/`APPROVED`/`REJECTED` Application for that Season (a prospective
 *     Applicant), THE Applicant_Portal presents this notice on the
 *     Contestant_Dashboard (R15.1) and the Contestant_Profile_Area (R15.2) — in
 *     place of an open Phase 1 application — and prevents initiating a Phase 1
 *     submission (R15.4).
 *   - The notice conveys that the current Season is underway and that sign-ups
 *     for the next Season will open soon (R15.3).
 *   - No notice when the Phase_1_Close_Date is absent, or `now` is before it, or
 *     the viewer already holds a decided/submitted Application (R15.5).
 *
 * The close-side decision reuses the shared pure `isDeadlineReached` from
 * `phaseDeadlines.ts` (the same exclusive `now >= closesAt` comparison the
 * server and `isIntakeOpen` use) — the comparison is never re-derived here.
 *
 * Requirements: 15.1, 15.2, 15.3, 15.4, 15.5
 */

import * as React from 'react';
import { CalendarClock } from 'lucide-react';

import type { Season } from '@/hooks/useActiveSeason';
import { isDeadlineReached } from '../../../../infrastructure/lambda/application-form/phaseDeadlines';

/**
 * The Application statuses that exclude a viewer from the notice while intake is
 * closed. `SUBMITTED`/`APPROVED` viewers are in-review or advancing and never see
 * a season-transition notice. `REJECTED` is deliberately NOT in this set: a
 * rejected viewer is *included* for season-gated closure messaging while the
 * current season's intake remains closed (application-decision-feedback R6.5),
 * and reverts to prospective-applicant messaging once intake reopens — handled by
 * the shared `isDeadlineReached` boundary below, with no extra status branch.
 */
const NON_PROSPECTIVE_STATUSES: ReadonlySet<string> = new Set([
  'SUBMITTED',
  'APPROVED',
]);

export interface ShouldShowSeasonTransitionNoticeArgs {
  /** The active Season (from `useActiveSeason`), or null while unresolved. */
  season: Season | null;
  /**
   * The viewer's own Application submission status for that Season, or
   * null/undefined when they have no Application yet. Any status other than
   * `SUBMITTED`/`APPROVED`/`REJECTED` (i.e. none or `DRAFT`) is prospective.
   */
  submissionStatus: string | null | undefined;
  /** The Evaluation_Instant. */
  now: Date;
}

/**
 * Whether the Season_Transition_Notice should be shown (pure decision).
 *
 * True iff the active Season carries a present Phase_1_Close_Date
 * (`intakeWindow.closesAt`), that close instant has been reached at `now`
 * (exclusive, via the shared `isDeadlineReached`), AND the viewer is either a
 * prospective Applicant (no `SUBMITTED`/`APPROVED`/`REJECTED` Application) or a
 * `REJECTED` viewer awaiting the next intake. `SUBMITTED`/`APPROVED` viewers are
 * always excluded. Once intake reopens for the resolved season
 * (`isDeadlineReached === false`), the gate returns false for every viewer,
 * reverting a rejected viewer to prospective-applicant messaging
 * (application-decision-feedback R6.1–R6.3, R6.5).
 */
export function shouldShowSeasonTransitionNotice({
  season,
  submissionStatus,
  now,
}: ShouldShowSeasonTransitionNoticeArgs): boolean {
  if (!season) return false;

  const closesAt = season.intakeWindow?.closesAt;
  // Phase 1 close absent → no notice (R15.5).
  if (closesAt == null || closesAt.trim() === '') return false;

  // Phase 1 still open (now < closesAt) → no notice (R15.5). This is also the
  // boundary that reverts a REJECTED viewer to prospective-applicant messaging
  // once the resolved season's intake reopens (R6.3).
  if (!isDeadlineReached(closesAt, now)) return false;

  // A viewer whose Application is SUBMITTED or APPROVED is in-review or advancing
  // and is never shown a season-transition notice. A REJECTED viewer is NOT
  // excluded here: while intake is closed they are included for season-gated
  // closure messaging (R6.5). A prospective Applicant (none/DRAFT) is included.
  if (submissionStatus != null && NON_PROSPECTIVE_STATUSES.has(submissionStatus)) {
    return false;
  }

  return true;
}

export interface SeasonTransitionNoticeProps {
  /** The active Season, used for its display name in the copy. */
  season?: Season | null;
  /**
   * The viewer's own Application submission status for the season. When
   * `REJECTED`, the notice presents season-gated **closure** messaging
   * ("we regret … reapply at the next intake") rather than the generic
   * prospective-applicant copy (application-decision-feedback R6.1, R6.2).
   * Any other value (including null/undefined) renders the prospective copy.
   * The rendering decision — whether the notice shows at all, and its reversion
   * to prospective messaging once intake reopens — is still owned by
   * {@link shouldShowSeasonTransitionNotice}, so both surfaces that gate on it
   * cannot disagree (R6.3, R6.4).
   */
  submissionStatus?: string | null;
}

/**
 * The presentational Season_Transition_Notice card. Callers gate rendering on
 * {@link shouldShowSeasonTransitionNotice}; this component only renders the
 * message (R15.3). It carries no application affordance — presenting it in place
 * of an open Phase 1 application is how the surface prevents initiating a Phase 1
 * submission (R15.4).
 *
 * Two copy variants share the same card:
 *   - **Rejected closure** (`submissionStatus === 'REJECTED'`): regrets the
 *     outcome and points the Contestant at the next season's intake
 *     (application-decision-feedback R6.1, R6.2).
 *   - **Prospective** (any other status): the season-underway "sign-ups will
 *     open soon" copy (season-phase-deadlines R15.3).
 */
export function SeasonTransitionNotice({
  season,
  submissionStatus,
}: SeasonTransitionNoticeProps = {}): React.ReactElement {
  const seasonName = season?.displayName?.trim();
  const isRejectedClosure = submissionStatus === 'REJECTED';

  return (
    <div
      className="flex items-center justify-center py-16 px-5"
      data-testid="season-transition-notice"
      data-variant={isRejectedClosure ? 'rejected-closure' : 'prospective'}
    >
      <div className="bg-card border border-border rounded-card shadow-subtle p-8 sm:p-10 text-center max-w-md">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CalendarClock className="h-7 w-7" />
        </div>

        {isRejectedClosure ? (
          <>
            <span className="text-eyebrow-sm text-muted-foreground mb-2 block">
              Application Closed
            </span>
            <h2 className="font-display text-page-title text-foreground not-italic">
              We regret to inform you
            </h2>
            <p className="font-sans text-body text-muted-foreground mt-4 max-w-prose mx-auto">
              {seasonName
                ? `Your application was not approved for the ${seasonName} season, `
                : 'Your application was not approved this season, '}
              and this season&rsquo;s intake is now closed. We&rsquo;d welcome you to
              reapply when sign-ups for the next season open — please check back then
              to begin a new application.
            </p>
          </>
        ) : (
          <>
            <span className="text-eyebrow-sm text-muted-foreground mb-2 block">
              Season Underway
            </span>
            <h2 className="font-display text-page-title text-foreground not-italic">
              {seasonName ? `The ${seasonName} season is underway` : 'This season is underway'}
            </h2>
            <p className="font-sans text-body text-muted-foreground mt-4 max-w-prose mx-auto">
              Sign-ups for the current season have closed and the competition is now
              underway. Sign-ups for the next season will open soon — please check
              back later to begin your application.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default SeasonTransitionNotice;
